import { Book, RuleAlias } from '@anschuetz-elog/common';
import { computed, Ref } from 'vue';

import useFeathers from '#/compositions/useFeathers';
import useFind from '#/compositions/useFind';
import { getLicenses, isLicenseValid } from '#/compositions/useLicenses';
import { useAuthStore } from '#/stores/auth';

type AccessibleBooks = Ref<(Book & { isLicensed: boolean })[]>;
type DetailedResponse = {
  books: AccessibleBooks;
  isLoading: Ref<boolean>;
  accessToAllBooks: Ref<boolean>;
};

/** returns all the books that are accessible by the user and also specifies if the book is licensed */
export function getBooksForUser(withDetails: true): DetailedResponse;
export function getBooksForUser(withDetails?: false): AccessibleBooks;
export function getBooksForUser(withDetails?: boolean): AccessibleBooks | DetailedResponse {
  const localCache = useFeathers().get('localCache');
  const authStore = useAuthStore();
  const isAuthenticated = computed(() => {
    return authStore.isAuthenticated;
  });

  const { data: configurations, isLoading } = useFind(
    'book-configuration',
    computed(() => (isAuthenticated.value ? {} : undefined)),
  );

  const books = computed(() => {
    if (!isAuthenticated.value) {
      return [];
    }
    return localCache.getBooks();
  });

  const configuration = computed(() => {
    return configurations.value.length > 0 ? configurations.value[0] : undefined;
  });

  const licenses = getLicenses();

  const activeBooks = computed(() =>
    books.value
      .filter((book) => !configuration.value?.deactivatedBooks.map((db) => db._id).includes(book._id))
      .sort(({ name }, { name: otherName }) => name.localeCompare(otherName))
      .map((book) => {
        const currentBook = { ...book, isLicensed: isLicenseValid(book.technicalName, licenses.value) };
        return currentBook;
      }),
  );

  const accessibleBooks = computed(() => {
    const user = authStore.user;
    if (!user) {
      return [];
    }

    if (
      user.roles.some(
        (role) =>
          role.name === 'admin' ||
          role.name === 'master' ||
          role.name === 'service' ||
          role.rules.some(({ alias }) => alias === RuleAlias.READ_ALL),
      )
    ) {
      return activeBooks.value;
    }

    return activeBooks.value.filter((book) =>
      user.roles.some(
        (role) =>
          (book.accessToBookRole !== undefined && role._id === book.accessToBookRole._id) ||
          role._id === book.secondApprovalRole._id,
      ),
    );
  });

  if (withDetails) {
    return {
      books: accessibleBooks,
      isLoading,
      accessToAllBooks: computed(() => accessibleBooks.value.length === books.value.length),
    };
  }
  return accessibleBooks;
}
